import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Shield, Zap, Ghost, Lock, Mail, Clock, ArrowRight, ArrowLeft,
  Copy, RefreshCw, Inbox, MailOpen, X, Check, Loader2, Plus, FileText
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
  const { user, token } = useAuth();
  const { t, getLocalizedPath } = useLanguage();
  const [tempEmail, setTempEmail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [addingAddress, setAddingAddress] = useState(false);
  const [latestPost, setLatestPost] = useState(null);

  useEffect(() => {
    // Check for existing session email first
    const savedEmail = sessionStorage.getItem('tempEmail');
    if (savedEmail) {
      try {
        const parsed = JSON.parse(savedEmail);
        // Check if not expired
        if (new Date(parsed.expires_at) > new Date()) {
          setTempEmail(parsed);
          setLoading(false);
          return;
        }
      } catch (e) {
        sessionStorage.removeItem('tempEmail');
      }
    }
    generateEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch latest blog post
  useEffect(() => {
    const fetchLatestPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/latest`);
        if (response.data) {
          setLatestPost(response.data);
        }
      } catch (error) {
        // Silently fail - no blog posts yet
      }
    };
    fetchLatestPost();
  }, []);

  const fetchInbox = useCallback(async () => {
    if (!tempEmail?.address) return;
    try {
      const response = await axios.get(`${API}/public/inbox/${encodeURIComponent(tempEmail.address)}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch inbox');
    }
  }, [tempEmail?.address]);

  useEffect(() => {
    if (tempEmail) {
      fetchInbox();
      // Auto-refresh inbox every 10 seconds
      const interval = setInterval(fetchInbox, 10000);
      return () => clearInterval(interval);
    }
  }, [tempEmail, fetchInbox]);

  const generateEmail = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/public/generate`);
      setTempEmail(response.data);
      sessionStorage.setItem('tempEmail', JSON.stringify(response.data));
      setMessages([]);
      setSelectedMessage(null);
    } catch (error) {
      toast.error('Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const refreshEmail = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API}/public/refresh`);
      setTempEmail(response.data);
      sessionStorage.setItem('tempEmail', JSON.stringify(response.data));
      setMessages([]);
      setSelectedMessage(null);
      toast.success(t('email.newGenerated'));
    } catch (error) {
      toast.error('Failed to generate new email');
    } finally {
      setRefreshing(false);
    }
  };

  const viewMessage = async (message) => {
    try {
      const response = await axios.get(`${API}/public/message/${message.id}`);
      setSelectedMessage(response.data);
      setMessages(messages.map(m => m.id === message.id ? { ...m, is_read: true } : m));
    } catch (error) {
      toast.error('Failed to load message');
    }
  };

  const copyEmail = () => {
    if (tempEmail?.address) {
      navigator.clipboard.writeText(tempEmail.address);
      setCopied(true);
      toast.success(t('email.copy'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const addAddressToAccount = async () => {
    if (!tempEmail) return;

    if (!user) {
      // Not logged in - save email to session and redirect to register
      sessionStorage.setItem('pendingEmailAddress', JSON.stringify(tempEmail));
      toast.info('Please register to save this address to your account');
      window.location.href = '/register';
      return;
    }

    // User is logged in - add to their account
    setAddingAddress(true);
    try {
      await axios.post(`${API}/emails/claim`, {
        email_id: tempEmail.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Address added to your account!');
      // Redirect to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add address';
      toast.error(message);
    } finally {
      setAddingAddress(false);
    }
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return '24h';
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  const features = [
    { icon: Shield, title: t('feature.privacy.title'), description: t('feature.privacy.desc') },
    { icon: Zap, title: t('feature.instant.title'), description: t('feature.instant.desc') },
    { icon: Ghost, title: t('feature.anonymous.title'), description: t('feature.anonymous.desc') },
    { icon: Clock, title: t('feature.autodelete.title'), description: t('feature.autodelete.desc') }
  ];

  return (
    <>
      <SEO
        useTranslation={true}
        description="meta.description"
        keywords="meta.keywords"
        path="/"
      />
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to={getLocalizedPath('/')} className="flex items-center gap-2">
              <Mail className="w-8 h-8 text-primary" />
              <span className="font-heading text-xl font-semibold text-foreground">Instant-Email</span>
            </Link>
            <div className="flex items-center gap-2">
              <LanguageSelector />
              {user ? (
                <Link to={getLocalizedPath('/dashboard')}>
                  <Button data-testid="go-to-dashboard-btn" className="rounded-full bg-primary text-primary-foreground hover:scale-105 active:scale-95 transition-all">
                    {t('nav.dashboard')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to={getLocalizedPath('/login')}>
                    <Button data-testid="login-btn" variant="ghost" className="text-foreground hover:text-primary transition-colors">
                      {t('nav.login')}
                    </Button>
                  </Link>
                  <Link to={getLocalizedPath('/register')}>
                    <Button data-testid="get-started-btn" className="rounded-full bg-primary text-primary-foreground hover:scale-105 active:scale-95 transition-all">
                      {t('nav.signup')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* Hero Section with Instant Email */}
        <section className="relative pt-28 pb-8 px-6">
          <div className="absolute inset-0 hero-glow pointer-events-none" />
          <div className="max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 border border-border mb-6">
                <Lock className="w-4 h-4 text-primary" />
                <span className="text-sm text-muted-foreground">{t('hero.badge')}</span>
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight mb-4">
                {t('hero.title')}
                <br />
                <span className="text-primary">{t('hero.titleHighlight')}</span>
              </h1>

              <p className="text-base text-muted-foreground max-w-xl mx-auto">
                {t('hero.subtitle')}
              </p>
            </div>

            {/* Email Display Card */}
            <Card className="bg-card border-border mb-6 overflow-hidden">
              <CardContent className="p-6">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Email Address Display */}
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <div className="flex-1 w-full">
                        <div
                          className="flex items-center gap-3 p-4 rounded-xl bg-secondary/50 border border-primary/30 cursor-pointer hover:border-primary transition-colors"
                          onClick={copyEmail}
                          data-testid="email-display"
                        >
                          <Mail className="w-6 h-6 text-primary flex-shrink-0" />
                          <span className="font-mono text-lg sm:text-xl text-foreground truncate flex-1">
                            {tempEmail?.address}
                          </span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className={`flex-shrink-0 ${copied ? 'text-green-400' : 'text-muted-foreground hover:text-primary'}`}
                            onClick={(e) => { e.stopPropagation(); copyEmail(); }}
                            data-testid="copy-email-btn"
                          >
                            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary/50 text-sm text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          {getTimeRemaining(tempEmail?.expires_at)}
                        </div>
                        <Button
                          size="icon"
                          variant="outline"
                          className="border-border hover:border-primary hover:text-primary"
                          onClick={refreshEmail}
                          disabled={refreshing}
                          data-testid="refresh-email-btn"
                        >
                          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                      <Button
                        onClick={addAddressToAccount}
                        disabled={addingAddress}
                        className="w-full sm:w-auto rounded-full bg-primary text-primary-foreground hover:scale-[1.02] active:scale-[0.98] transition-all"
                        data-testid="add-address-btn"
                      >
                        {addingAddress ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        {user ? t('email.addToAccount') : t('email.saveAddress')}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        {user ? t('email.addTip') : t('email.saveTip')}
                      </p>
                    </div>

                    {/* Quick tip */}
                    <p className="text-xs text-muted-foreground text-center">
                      {t('email.quickTip')}
                    </p>

                    {/* Latest announcement */}
                    {latestPost && (
                      <Link
                        to={getLocalizedPath(`/blog/${latestPost.slug}`)}
                        className="flex items-center gap-2 justify-center text-sm text-primary hover:underline mt-2 transition-all hover:gap-3"
                        data-testid="latest-announcement-link"
                      >
                        <FileText className="w-4 h-4" />
                        <span>{latestPost.title}</span>
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inbox Section */}
            <Card className="bg-card border-border overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <Inbox className="w-5 h-5 text-primary" />
                  <span className="font-heading font-semibold">{t('inbox.title')}</span>
                  {unreadCount > 0 && (
                    <Badge className="bg-primary text-primary-foreground">{unreadCount} {t('inbox.new')}</Badge>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={fetchInbox}
                  data-testid="refresh-inbox-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('email.refresh')}
                </Button>
              </div>

              <div className="flex h-[500px]">
                {/* Message List */}
                <div className="w-full sm:w-1/2 lg:w-1/3 border-r border-border">
                  <ScrollArea className="h-full">
                    {messages.length > 0 ? (
                      <div className="p-2">
                        {messages.map(message => (
                          <div
                            key={message.id}
                            className={`p-3 rounded-lg cursor-pointer transition-all mb-1 ${selectedMessage?.id === message.id
                              ? 'bg-secondary border border-primary/50'
                              : 'hover:bg-secondary/50'
                              } ${!message.is_read ? 'border-l-2 border-l-primary' : ''}`}
                            onClick={() => viewMessage(message)}
                            data-testid={`message-item-${message.id}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {!message.is_read && <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />}
                                  <p className={`text-sm truncate ${!message.is_read ? 'font-semibold' : ''}`}>
                                    {message.from_address}
                                  </p>
                                </div>
                                <p className="text-sm font-medium truncate mt-1">{message.subject}</p>
                                <p className="text-xs text-muted-foreground truncate mt-1">
                                  {message.body?.substring(0, 60)}...
                                </p>
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatTime(message.received_at)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-center p-6">
                        <div className="w-16 h-16 rounded-full bg-secondary/50 flex items-center justify-center mb-4">
                          <Inbox className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <p className="text-muted-foreground font-medium">{t('inbox.empty')}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t('inbox.emptyDesc')}
                        </p>
                      </div>
                    )}
                  </ScrollArea>
                </div>

                {/* Message Detail */}
                <div className="hidden sm:flex flex-1 flex-col">
                  {selectedMessage ? (
                    <>
                      <div className="p-4 border-b border-border flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                            <MailOpen className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{selectedMessage.from_address}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(selectedMessage.received_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setSelectedMessage(null)}
                          data-testid="close-message-btn"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="p-4 border-b border-border">
                        <h2 className="font-heading font-semibold">{selectedMessage.subject}</h2>
                        <p className="text-xs text-muted-foreground mt-1">To: {selectedMessage.to_address}</p>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4">
                        {selectedMessage.html_body ? (
                          <div
                            className="prose prose-invert prose-sm max-w-none min-h-full [&_img]:max-w-full [&_img]:h-auto [&_table]:w-full [&_table]:table-auto"
                            dangerouslySetInnerHTML={{ __html: selectedMessage.html_body }}
                          />
                        ) : (
                          <div className="whitespace-pre-wrap text-sm text-foreground min-h-full">
                            {selectedMessage.body}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
                      <div className="w-16 h-16 rounded-full bg-secondary/50 flex items-center justify-center mb-4">
                        <Shield className="w-8 h-8 text-primary" />
                      </div>
                      <p className="text-muted-foreground font-medium">Select an email to read</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Your privacy is protected
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Mobile message view */}
            {selectedMessage && (
              <div className="sm:hidden fixed inset-0 z-50 bg-background overflow-hidden">
                <div className="flex flex-col h-full max-h-screen overflow-hidden">
                  <div className="p-4 border-b border-border flex items-center justify-between glass sticky top-0 bg-background/95 backdrop-blur-sm flex-shrink-0">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Button
                        size="lg"
                        variant="ghost"
                        onClick={() => setSelectedMessage(null)}
                        className="flex items-center gap-2 hover:bg-secondary"
                      >
                        <ArrowLeft className="w-5 h-5" />
                        <span className="text-sm font-medium">Back</span>
                      </Button>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setSelectedMessage(null)}
                      className="flex-shrink-0 ml-2"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  <div className="p-4 border-b border-border bg-background flex-shrink-0">
                    <p className="text-xs text-muted-foreground mb-1">{selectedMessage.from_address}</p>
                    <h2 className="font-heading font-semibold text-base">{selectedMessage.subject}</h2>
                    <p className="text-xs text-muted-foreground mt-1">{formatTime(selectedMessage.received_at)}</p>
                  </div>
                  <div
                    className="flex-1 overflow-y-auto p-4 bg-background overscroll-contain"
                    style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
                  >
                    {selectedMessage.html_body ? (
                      <div
                        className="prose prose-invert prose-sm max-w-none [&_img]:max-w-full [&_img]:h-auto [&_table]:w-full [&_table]:table-auto [&]:text-foreground pb-8"
                        dangerouslySetInnerHTML={{ __html: selectedMessage.html_body }}
                      />
                    ) : (
                      <div className="whitespace-pre-wrap text-sm text-foreground pb-8">{selectedMessage.body}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="p-4 rounded-xl bg-card border border-border/50 hover:border-primary/50 transition-all"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                    <feature.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-4">
              {t('cta.title')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('cta.description')}
            </p>
            <Link to={getLocalizedPath('/register')}>
              <Button
                data-testid="cta-get-started-btn"
                size="lg"
                className="rounded-full bg-primary text-primary-foreground px-8 hover:scale-105 active:scale-95 transition-all"
              >
                {t('cta.button')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Landing;
